import PropTypes from 'prop-types';
import { forwardRef, useEffect, useCallback, useRef } from 'react';
import { useModal } from '../../../../../context/ModalContext';
import { useRoomContext } from '../../../../../context/RoomContext';
import { getAttachmentIcon } from '../../../../../utils/fileUtils';
import { isValidRoomId } from '../../../../../utils/matrixUtils';
import { createGeoUri } from '../../../../../services/locationService';
import useSendTypingIndicator from '../../../../../hooks/useSendTypingIndicator';
import useMentionAutocomplete from '../../../../../hooks/useMentionAutocomplete';
import MentionAutocomplete from '../../../../../components/MentionAutocomplete';
import AttachmentModal from './AttachmentModal';
import { getMessageRestrictionReason } from '../../../../../utils/roomUtils';
import { MESSAGE_TYPES } from '../../../../../constants';
import logger from '../../../../../utils/logger';
import './MessageInput.css';
import './Messages.css'; // Import for compose-reply-preview styles
import { canSendMessages } from '../../../../../utils/roomUtils';
import { useI18n } from '../../../../../context/I18nContext';


const MessageInput = forwardRef(({
  roomId,
  disabled = false,
  disableSend = false, // Separate control for send button
  allowEmptySend = false, // Allow sending empty messages (for NewChatView room creation)
  message = '',
  placeholder = 'Type a message...',
  replyingTo = null,
  editingMessage = null,
  onCancelReply = () => {},
  onCancelEdit = () => {},
  onSetMessage = (_messageText) => {},
  onSendMessage = () => {},
  onSendLocationMessage = null, // Optional: Function to send location messages directly
  onTriggerFileSelect = () => {},
  isSending = false, // Disable input during send to prevent race conditions
  // Message queue state (CLIENT A_25423)
  isQueueBusy = false,
  hasUnsentMessages = false,
  onRetryUnsent = () => {},
  onCancelUnsent = () => {},
}, ref) => {
  const { t } = useI18n();
  const { onMessageSent, onTextChange } = useSendTypingIndicator(roomId);
  const { showComponent, closeModal } = useModal();

  const roomContext = useRoomContext();
  const isServerNotices = roomContext?.isServerNotices ?? false;
  const client = roomContext?.client;
  const room = roomContext?.room;
  const members = roomContext?.members ?? [];
  const currentUserId = client?.getUserId?.();

  // Initialize mention autocomplete hook (excludes current user to prevent self-mentions)
  const {
    isOpen: isMentionOpen,
    suggestions: mentionSuggestions,
    selectedIndex: mentionSelectedIndex,
    handleInputChange: handleMentionInput,
    handleKeyDown: handleMentionKeyDown,
    selectMention,
    close: closeMentionAutocomplete,
  } = useMentionAutocomplete(members, currentUserId);

  // Keep a ref to the latest replyingTo to avoid stale closures in modal callbacks
  const replyingToRef = useRef(replyingTo);
  useEffect(() => { replyingToRef.current = replyingTo; }, [replyingTo]);

  // Check if user has permission to send messages
  // Take care of new chat view and loading states
  const hasPermission = !room || !client || canSendMessages(room, client);
  
  const restrictionReasonKey = getMessageRestrictionReason(room, client);
  const restrictionReason = restrictionReasonKey ? t(restrictionReasonKey) : null;

  // Simple room ID validation
  const isRoomIdInvalid = !isValidRoomId(roomId);

  // Disable ENTIRE compose area if: Server Notices room or 
  // User lacks send permission
  const isComposeDisabled = disabled || isServerNotices || !hasPermission;
  // Disable send while queue is actively retrying (CLIENT A_25423)
  const isSendDisabled = disableSend || isSending || isQueueBusy || isComposeDisabled;
  const isAttachmentDisabled = isComposeDisabled || isRoomIdInvalid || isQueueBusy;
  
  // Update placeholder based on restriction
  const effectivePlaceholder = isServerNotices
    ? t('Messages.serverNoticesRestriction', {}, 'Cannot send messages in Server Notices room')
    : !hasPermission
      ? t('Messages.noSendPermission', {}, 'You don\'t have permission to send messages in this room')
      : placeholder;
  
  // Tooltip for disabled states
  const sendButtonTitle = restrictionReason || t('ChatViewFooter.sendMessage', {}, 'Send message');


  // Auto-resize textarea based on content
  const adjustTextareaHeight = useCallback(() => {
    if (ref && ref.current) {
      // Reset height to min-height to get the correct scrollHeight
      ref.current.style.height = '77px';
      
      // Only grow if scrollHeight exceeds min-height
      if (ref.current.scrollHeight > 77) {
        const newHeight = Math.min(ref.current.scrollHeight, 200); // Max height of 200px
        ref.current.style.height = `${newHeight}px`;
      }
    }
  }, [ref]);

  // Adjust height when message changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [message, adjustTextareaHeight]);

  /**
   * Handle mention selection from autocomplete
   * Can be called with a member object (click selection) or
   * a selection result object (keyboard selection with mentionText/indices)
   */
  const handleMentionSelect = useCallback((memberOrResult) => {
    let mentionText, startIndex, endIndex;

    if (memberOrResult.mentionText) {
      // Called with selection result from keyboard (Enter/Tab)
      ({ mentionText, startIndex, endIndex } = memberOrResult);
    } else {
      // Called with member object from click
      const result = selectMention(memberOrResult);
      if (!result) return;
      ({ mentionText, startIndex, endIndex } = result);
    }

    // Replace the @query with the mention text plus a trailing space
    const beforeMention = message.slice(0, startIndex);
    const afterMention = message.slice(endIndex);
    const newMessage = beforeMention + mentionText + ' ' + afterMention;
    onSetMessage(newMessage);
    closeMentionAutocomplete();

    // Focus back to input and set cursor after mention
    if (ref?.current) {
      ref.current.focus();
      // Set cursor position after the inserted mention and space
      const newCursorPos = startIndex + mentionText.length + 1;
      setTimeout(() => {
        ref.current?.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  }, [message, onSetMessage, selectMention, closeMentionAutocomplete, ref]);

  const onKeyDown = (e) => {
    // Let mention autocomplete handle keyboard events first
    const mentionResult = handleMentionKeyDown(e);
    if (mentionResult) {
      // If Enter/Tab returned a selection result (object with mentionText), insert it
      if (typeof mentionResult === 'object' && mentionResult.mentionText) {
        handleMentionSelect(mentionResult);
      }
      // Event was consumed by autocomplete (arrow keys, escape, or selection)
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Keep keyboard send behavior aligned with the send button disabled state.
      if (isSendDisabled) return;
      onMessageSent();
      // Don't clear here - let parent handle clearing after successful send
      onSendMessage();
    }
  };

  const onChange = (e) => {
    // Don't process input if compose area is disabled
    if (isComposeDisabled) return;

    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    onSetMessage(newValue);

    // Handle typing indicators
    onTextChange(newValue);

    // Check for @mention trigger
    handleMentionInput(newValue, cursorPosition);
  };

  const handleSendClick = () => {
    // Don't send if currently sending
    if (isSending || isComposeDisabled) return;
    onMessageSent();
    // Don't clear here - let parent handle clearing after successful send
    onSendMessage();
  };

  const handleAttachFile = () => {
    closeModal();
    onTriggerFileSelect();
  };

  /**
   * Handle location sharing
   * Called when user successfully shares their current location
   * @param {Object} locationData - Raw location data from geolocation API
   * @param {string} locationUrl - Formatted URL for the location
   */
  const handleShareCurrentLocation = async (locationData, locationUrl) => {
    closeModal();
    
    logger.debug('[MessageInput] Location shared, sending m.location message');
    
    // If we have a direct send function, send location message immediately
    if (onSendLocationMessage && typeof onSendLocationMessage === 'function') {
      const { latitude, longitude } = locationData;
      const geoUri = createGeoUri(latitude, longitude);
      
      const locationContent = {
        msgtype: 'm.location',
        body: `Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
        geo_uri: geoUri,
      };
      
      // Use ref to get the latest replyingTo value (avoids stale closure in modal callbacks)
      const currentReplyingTo = replyingToRef.current;
      if (currentReplyingTo && currentReplyingTo.id) {
        logger.debug('[MessageInput] Adding reply metadata to location message');
        locationContent['m.relates_to'] = {
          'm.in_reply_to': {
            event_id: currentReplyingTo.id,
          },
        };
      }

      try {
        await onSendLocationMessage(locationContent);
        logger.debug('[MessageInput] Location message sent successfully');

        // Clear reply state if we were replying
        if (currentReplyingTo) {
          onCancelReply();
        }
      } catch (error) {
        logger.error('[MessageInput] Failed to send location message:', error);
      }
    } else {
      // Fallback: insert location URL as text if direct send not available
      logger.debug('[MessageInput] Falling back to text location message');
      const locationMessage = `📍 My Location: ${locationUrl}`;
      onSetMessage(locationMessage);
      
      // Focus back to input so user can add text or send immediately
      if (ref?.current) {
        ref.current.focus();
      }
    }
  };

  const handleAttachmentClick = () => {
    if (isAttachmentDisabled) return;

    showComponent(
      <AttachmentModal
        onAttachFileClick={handleAttachFile}
        onShareCurrentLocation={handleShareCurrentLocation}
        onClose={closeModal}
        replyingTo={replyingTo}
      />,
      {
        title: t('ChatOptionsView.addAttachmentTitle', {}, 'Add Attachment'),
        size: 'medium',
        closable: true,
      },
    );
  };

  // Auto-cancel edit when send permissions are revoked
  useEffect(() => {
    // Only check if we're actively editing a message
    if (!editingMessage || !room || !client) return;
    
    // Check if user still has permission to send messages
    const hasPermission = canSendMessages(room, client);
    
    if (!hasPermission) {
      logger.info('[MessageInput] Send permission revoked while editing, canceling edit');
      onCancelEdit();
    }
  }, [editingMessage, room, client, roomContext?.powerLevelsCounter, onCancelEdit]);

  const renderReplyPreview = () => {
    if (!replyingTo) return null;

    const replyLabel = t(
      'MessageInput.replyingTo',
      { name: replyingTo.senderName || t('MessageInput.unknownUser', {}, 'Unknown') },
      `Replying to ${replyingTo.senderName || 'Unknown'}`,
    );

    // Special compact layout for attachment replies
    if (replyingTo.isAttachment) {
      // Use the thumbnail if it's an image, otherwise get the generic icon
      const showThumbnail = replyingTo.msgtype === 'm.image' && replyingTo.thumbnailUrl;
      const iconSrc = showThumbnail
        ? replyingTo.thumbnailUrl
        : getAttachmentIcon(replyingTo.msgtype, replyingTo.filename);

      return (
        <div className="compose-reply-preview attachment-reply">
          {/* Use 'attachment-reply-thumbnail' class for proper styling of image previews */}
          <img src={iconSrc} alt="attachment icon" className={showThumbnail ? 'attachment-reply-thumbnail' : 'attachment-reply-icon'} />
          <div className="compose-reply-preview-content">
            <div className="compose-reply-preview-header">
              <span className="compose-reply-preview-label">{replyLabel}</span>
              <button className="compose-reply-preview-close" onClick={onCancelReply}>
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="compose-reply-preview-message attachment-fileinfo">
              {replyingTo.filename} ({(replyingTo.size)})
            </div>
          </div>
        </div>
      );
    }

    // Default layout for text replies
    // Format emote replies with asterisk prefix: "* SenderName action"
    const displayContent = replyingTo.msgtype === MESSAGE_TYPES.EMOTE
      ? `* ${replyingTo.senderName} ${replyingTo.content}`
      : replyingTo.content;

    return (
      <div className="compose-reply-preview">
        <div className="compose-reply-preview-content">
          <div className="compose-reply-preview-header">
            <span className="compose-reply-preview-label">{replyLabel}</span>
            <button className="compose-reply-preview-close" onClick={onCancelReply}>
              <i className="fa fa-times" />
            </button>
          </div>
          <div className="compose-reply-preview-message">
            {displayContent}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="message-input-container">
      {/* Unsent messages banner (CLIENT A_25423) */}
      {hasUnsentMessages && (
        <div className="message-queue-unsent-banner" role="alert">
          <span className="message-queue-unsent-text">
            <i className="fa fa-exclamation-triangle" />
            {' '}
            {t('MessageInput.unsentMessages', {}, 'Some messages could not be sent.')}
          </span>
          <div className="message-queue-action-group">
            <button
              className="message-queue-retry-button"
              onClick={onRetryUnsent}
              type="button"
            >
              {t('MessageInput.retryAll', {}, 'Retry')}
            </button>
            <button
              className="message-queue-cancel-button"
              onClick={onCancelUnsent}
              type="button"
            >
              {t('MessageInput.cancelUnsent', {}, 'Delete')}
            </button>
          </div>
        </div>
      )}
      {renderReplyPreview()}
      {editingMessage && (
        <div className="compose-reply-preview">
          <div className="compose-reply-preview-content">
            <div className="compose-reply-preview-header">
              <span className="compose-reply-preview-label">{t('MessageInput.editingMessage', {}, 'Editing message')}</span>
              <button className="compose-reply-preview-close" onClick={onCancelEdit}>
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="compose-reply-preview-message">
              {t('MessageInput.originalMessage', {}, 'Original:')} {editingMessage.originalContent}
            </div>
          </div>
        </div>
      )}
      <MentionAutocomplete
        isOpen={isMentionOpen}
        suggestions={mentionSuggestions}
        selectedIndex={mentionSelectedIndex}
        onSelect={handleMentionSelect}
        onClose={closeMentionAutocomplete}
        position={{ x: 8, y: -8 }}
      />
      <div className="message-input-wrapper">
        <button
          className="message-input-attachment-button"
          onClick={handleAttachmentClick}
          disabled={isAttachmentDisabled}
          aria-label={
            isRoomIdInvalid 
              ? t('ChatViewFooter.attachFileSelectConversation', {}, 'Attach file - Select a conversation first')
              : restrictionReason
                ? restrictionReason
                : t('ChatViewFooter.attachFile', {}, 'Attach file')
          }
          title={
            isRoomIdInvalid 
              ? t('ChatViewFooter.selectConversationToAttach', {}, 'Select a conversation to attach files')
              : restrictionReason
                ? restrictionReason
                : t('ChatViewFooter.attachFile')
          }
        >
          <span></span>
        </button>
        <textarea
          ref={ref}
          className="message-input"
          placeholder={effectivePlaceholder}
          disabled={isComposeDisabled}
          value={message}
          onChange={onChange}
          onKeyDown={onKeyDown}
          title={restrictionReason || undefined}
          style={{ cursor: isComposeDisabled ? 'not-allowed' : 'text' }}
        />
        <button
          className="message-send-button"
          onClick={handleSendClick}
          disabled={isSendDisabled || (!allowEmptySend && (!message || typeof message !== 'string' || !message.trim()))}
          type="button"
          title={sendButtonTitle}
        >
          <i className="fa fa-paper-plane" />
        </button>
      </div>
    </div>
  );
});

MessageInput.displayName = 'MessageInput';

MessageInput.propTypes = {
  roomId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  disableSend: PropTypes.bool,
  allowEmptySend: PropTypes.bool,
  message: PropTypes.string,
  placeholder: PropTypes.string,
  replyingTo: PropTypes.shape({
    id: PropTypes.string,
    senderName: PropTypes.string,
    content: PropTypes.string,
    isAttachment: PropTypes.bool,
    msgtype: PropTypes.string,
    filename: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    size: PropTypes.string,
  }),
  editingMessage: PropTypes.shape({
    id: PropTypes.string,
    originalContent: PropTypes.string,
    originalEvent: PropTypes.object,
  }),
  onCancelReply: PropTypes.func,
  onCancelEdit: PropTypes.func,
  onSetMessage: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onSendLocationMessage: PropTypes.func,
  onTriggerFileSelect: PropTypes.func,
  isSending: PropTypes.bool,
  // Message queue state (CLIENT A_25423)
  isQueueBusy: PropTypes.bool,
  hasUnsentMessages: PropTypes.bool,
  onRetryUnsent: PropTypes.func,
  onCancelUnsent: PropTypes.func,
};

export default MessageInput;
